import { useEffect, useState } from "react"

const findScrollParent = (el: HTMLElement | null): HTMLElement | null => {
  let node = el?.parentElement ?? null

  while (node) {
    if (["auto", "scroll"].includes(getComputedStyle(node).overflowY)) {
      return node
    }
    node = node.parentElement
  }

  return null
}

// Same idea as BadgeTemplateBrowser's useScrollSpy (which section's top has scrolled past a
// small threshold), simplified for a single always-mounted, natively-scrolling container —
// no SimpleBar coupling or cross-panel scroll-target deferral needed here.
//
// Retries via rAF until the sections/scroll container actually exist in the DOM — this runs
// inside a modal whose content depends on data (the template) that may not be loaded yet on
// the effect's first run, so a one-shot lookup would silently never attach.
const useScrollSpy = (sectionIds: string[]): string | null => {
  const [activeId, setActiveId] = useState<string | null>(sectionIds[0] ?? null)

  useEffect(() => {
    let cancelled = false
    let detach: (() => void) | undefined

    const attach = () => {
      if (cancelled) {
        return
      }

      const container = findScrollParent(document.getElementById(sectionIds[0]))
      if (!container) {
        requestAnimationFrame(attach)
        return
      }

      let ticking = false

      const updateActive = () => {
        // A flat pixel threshold breaks for a short trailing section: scrollIntoView can't
        // scroll its top all the way up to the container's edge once there's no more content
        // below it to make room, so it can land 100px+ short. Use a fraction of the visible
        // height instead — activates whichever section has crossed ~30% down the viewport,
        // same standard scrollspy technique, robust regardless of how much trailing space
        // the last section has to scroll into.
        const containerTop = container.getBoundingClientRect().top
        const threshold = containerTop + container.clientHeight * 0.3
        let current = sectionIds[0] ?? null

        sectionIds.forEach((id) => {
          const el = document.getElementById(id)
          if (el && el.getBoundingClientRect().top <= threshold) {
            current = id
          }
        })

        // Same reasoning as the threshold above, relative rather than a fixed pixel slack —
        // a short last section can leave a gap of a couple hundred px between max scroll and
        // its top actually reaching the primary threshold.
        const remaining = container.scrollHeight - (container.scrollTop + container.clientHeight)
        const atBottom = remaining <= container.clientHeight * 0.4
        setActiveId(atBottom ? sectionIds[sectionIds.length - 1] ?? current : current)
      }

      const handleScroll = () => {
        if (!ticking) {
          ticking = true
          requestAnimationFrame(() => {
            updateActive()
            ticking = false
          })
        }
      }

      container.addEventListener("scroll", handleScroll, { passive: true })
      updateActive()
      detach = () => container.removeEventListener("scroll", handleScroll)
    }

    attach()

    return () => {
      cancelled = true
      detach?.()
    }
  }, [sectionIds])

  return activeId
}

export default useScrollSpy
